import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { AuthenticateStatus } from 'components/authenticate/authenticate_status';
import { AuthenticateStore, ServerStore } from '@seedlang/stores';
import { AppUI, TreeUI } from '@seedlang/state';
import { isBlank, isPresent, getCookieValue } from '@seedlang/utils';
import Alert from 'components/alert';
import Button from 'components/button/button';
import { Theme, Constants } from '@seedlang/constants';
import Text from 'components/text';
import styled from '@emotion/styled';
import Checkbox from 'components/checkbox';
import Spinner from 'components/spinner';
import TextDisplay, { markdownToPlainText } from 'components/text_display';
import { gaps } from 'components/layout/stack';
import EmailTypoWarning from 'components/form/email_typo_warning';

const PrivacyLink = styled.a`
  font-size: 12px;
  text-decoration: underline;
  width: 100%;
  text-align: center;
  display: block;
  border-top: 1px solid #d9dadc;
  padding-top: 10px;
  margin-top: 10px;
  color: black;
`;

const InputWrapper = styled.span`
`;

const Terms = styled.div`
  margin: 15px 0 10px;
  text-align: ${props => props.textAlign};
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 10px;
  align-items: start;
  font-size: 12px;
  line-height: 1.5;
`;

const EmailDisclosure = styled.p`
  color: ${Theme.gray};
  font-size: 12px;
  line-height: 1.5;
  margin: 15px 0 10px;
`;

const MailerlitePreference = styled(Terms)`
  font-size: 12px;
  margin: 10px 0;
`;

const MailerlitePreferenceText = styled.div`
  padding-top: 2px;
`;

const PasswordFieldWrapper = styled.div`
  position: relative;
  margin-bottom: ${gaps.sm};
  input {
    margin-bottom: 0 !important;
    padding-right: ${gaps.xl} !important;
  }
`;

const PasswordToggle = styled.button`
  width: fit-content !important;
  position: absolute;
  right: 10px;
  top: calc(50% + 2px);
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  color: ${Theme.gray};

  &:hover {
    color: ${Theme.darkGray};
  }
`;

const RegistrationTerms = ({
  accentColor,
  children,
  onToggleTerms,
  safePrivacyUrl,
  safeTermsOfServiceUrl,
  siteIsSeedlang,
  teamName,
  termsAccepted,
}) => {
  const seedlangTermsUrl = siteIsSeedlang ? '/terms' : 'https://www.seedlang.com/terms';
  const seedlangPrivacyUrl = siteIsSeedlang ? '/privacy' : 'https://www.seedlang.com/privacy';
  const hasMemrevLegalLinks = Boolean(safeTermsOfServiceUrl || safePrivacyUrl);
  const checkboxColor = siteIsSeedlang ? Theme.blue : accentColor;
  const learningLanguage = teamName?.replace(/^Easy /, '');

  return (
    <>
      <Terms>
        <Checkbox
          value={termsAccepted}
          onClick={onToggleTerms}
          selectedBackground={checkboxColor}
        />
        <span>
          {
            siteIsSeedlang ? (
              <span>
                I agree to the <a className='underline' href={seedlangTermsUrl} target='_blank' rel='noopener noreferrer'>Terms of Service</a> & <a className='underline' href={seedlangPrivacyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>.
              </span>
            ) : (
              <>
                <span>I agree to the </span>
                {
                  hasMemrevLegalLinks && (
                    <>
                      {safeTermsOfServiceUrl && <a className='underline' href={safeTermsOfServiceUrl} target='_blank' rel='noopener noreferrer'>Terms</a>}
                      {safeTermsOfServiceUrl && safePrivacyUrl && <span> & </span>}
                      {safePrivacyUrl && <a className='underline' href={safePrivacyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>}
                      <span> of Easy Languages GmbH, and the </span>
                    </>
                  )
                }
                <a className='underline' href={seedlangTermsUrl} target='_blank' rel='noopener noreferrer'>Terms</a>
                <span> & </span>
                <a className='underline' href={seedlangPrivacyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
                <span> of the learning platform Seedlang.</span>
              </>
            )
          }
        </span>
      </Terms>
      {children}
      {
        !siteIsSeedlang &&
          <EmailDisclosure>
            Besides account emails, {teamName} will send you tips for using the platform and learning {learningLanguage}, plus its own offers. You can unsubscribe for free anytime.
          </EmailDisclosure>
      }
    </>
  );
};

export const RegistrationForm = ({
  accentColor,
  buttonBackground,
  color,
  confirmPassword,
  confirmPasswordFocused,
  disabled,
  email,
  emailFocused,
  enableMailerlite,
  errorMessage,
  hidePrivacy,
  hideTitle,
  invalidEmail,
  mailerliteGroups = [],
  name,
  onBlurConfirmPassword,
  onBlurEmail,
  onBlurPassword,
  onChangeConfirmPassword,
  onChangeEmail,
  onChangeName,
  onChangePassword,
  onFocusConfirmPassword,
  onFocusEmail,
  onFocusPassword,
  onSubmit,
  onToggleMailerliteGroup,
  onTogglePasswordVisibility,
  onToggleTerms,
  password,
  passwordFocused,
  safePrivacyUrl,
  safeTermsOfServiceUrl,
  selectedMailerliteGroupIds = [],
  siteIsSeedlang,
  submitting,
  successMessage,
  teamName,
  termsAccepted,
  unmaskPassword,
}) => (
  <span>
    {
      submitting &&
        <Spinner
          background={siteIsSeedlang ? Theme.blue : accentColor}
        />
    }
    {
      !submitting && !hideTitle &&
        <Text
          heading='3'
          center
          margin='0 0 10px 0'
          color='#333'
        >
          Create Account
        </Text>
    }
    <form acceptCharset='UTF-8' onSubmit={onSubmit} id='new_user' method='post' autoComplete='off'>
      <AuthenticateStatus
        errorMessage={errorMessage}
        successMessage={successMessage}
      />
      <InputWrapper color={color || '#333'}>
        <div className='form-email'>
          <label htmlFor='name'>Name</label>
          <input
            type='text'
            autoComplete='off'
            noValidate
            value={name}
            onChange={el => onChangeName(el.target.value)}
            id='name'
            maxLength={Constants.MAX_NAME_LENGTH}
          />
        </div>
        <div className='form-email'>
          <label htmlFor='email'>Email</label>
          <input
            onFocus={onFocusEmail}
            onBlur={onBlurEmail}
            autoComplete='off'
            noValidate
            value={email}
            onChange={el => onChangeEmail(el.target.value)}
            type='email'
            id='email'
          />
          {
            isPresent(email) && invalidEmail && !emailFocused &&
              <Alert>
                This email address is invalid. Please try another.
              </Alert>
          }
          <EmailTypoWarning email={email} focused={emailFocused} />
        </div>
        <div className='form-password'>
          <label htmlFor='user_password'>Password</label>
          <PasswordFieldWrapper>
            <input
              onFocus={onFocusPassword}
              onBlur={onBlurPassword}
              noValidate
              autoComplete='off'
              id='user_password'
              title='8 characters minimum'
              pattern='.{8,}'
              type={unmaskPassword ? 'text' : 'password'}
              value={password}
              onChange={el => onChangePassword(el.target.value)}
            />
            {password && (
              <PasswordToggle
                type='button'
                onClick={onTogglePasswordVisibility}
                tabIndex={-1}
              >
                <i className={`fa ${unmaskPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
              </PasswordToggle>
            )}
          </PasswordFieldWrapper>
          {
            isPresent(password) && password.length < 8 && !passwordFocused &&
              <Alert>
                Password needs to be at least 8 characters long
              </Alert>
          }
        </div>
        {
          !siteIsSeedlang &&
            <div className='form-password'>
              <label htmlFor='confirm_password'>Confirm Password</label>
              <PasswordFieldWrapper>
                <input
                  onFocus={onFocusConfirmPassword}
                  onBlur={onBlurConfirmPassword}
                  noValidate
                  autoComplete='off'
                  id='confirm_password'
                  type={unmaskPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={el => onChangeConfirmPassword(el.target.value)}
                />
                {confirmPassword && (
                  <PasswordToggle
                    type='button'
                    onClick={onTogglePasswordVisibility}
                    tabIndex={-1}
                  >
                    <i className={`fa ${unmaskPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </PasswordToggle>
                )}
              </PasswordFieldWrapper>
              {
                isPresent(confirmPassword) && password !== confirmPassword && !confirmPasswordFocused &&
                  <Alert>
                    Passwords don't match
                  </Alert>
              }
            </div>
        }
      </InputWrapper>
      <RegistrationTerms
        accentColor={accentColor}
        onToggleTerms={onToggleTerms}
        safePrivacyUrl={safePrivacyUrl}
        safeTermsOfServiceUrl={safeTermsOfServiceUrl}
        siteIsSeedlang={siteIsSeedlang}
        teamName={teamName}
        termsAccepted={termsAccepted}
      >
        {
          !siteIsSeedlang && enableMailerlite && mailerliteGroups.filter(group => group.helperText).map(group => (
            <MailerlitePreference key={group.id}>
              <Checkbox
                ariaLabel={markdownToPlainText(group.helperText)}
                onClick={() => onToggleMailerliteGroup(group.id)}
                selectedBackground={accentColor}
                value={selectedMailerliteGroupIds.includes(group.id)}
              />
              <MailerlitePreferenceText>
                <TextDisplay
                  linkColor='black'
                  markdown={group.helperText}
                  removeTopMargin
                />
              </MailerlitePreferenceText>
            </MailerlitePreference>
          ))
        }
      </RegistrationTerms>

      <Button
        margin='10px 0 0 0'
        onClick={onSubmit}
        disabled={disabled}
        height='50px'
        width='100%'
        borderRadius='50px'
        background={buttonBackground}
        color='#FFF'
      >
        Create Account
      </Button>
    </form>

    {!hidePrivacy &&
      <PrivacyLink href='/privacy'>
        View Our Privacy Page
      </PrivacyLink>}
  </span>
);

@observer
class RegistrationFormWrapper extends React.Component {
  @observable email = '';
  @observable password = '';
  @observable confirmPassword = '';
  @observable name = '';
  @observable termsAccepted = false;
  @observable selectedMailerliteGroupIds = [];
  @observable invalidEmail = false;
  @observable emailFocused = false;
  @observable passwordFocused = false;
  @observable confirmPasswordFocused = false;
  @observable unmaskPassword = false;
  @observable submitting = false;
  @observable alreadyExists = false;
  @observable isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

  @computed get buttonBackground() {
    if (AppUI.siteIsMemrev) {
      return AppUI.site.accentColor;
    }
    return Theme.blue;
  }

  @computed get disabled() {
    const passwordMismatch = AppUI.siteIsMemrev && this.password !== this.confirmPassword;
    return this.submitting || this.invalidEmail || isBlank(this.password) || this.password.length < 8 || isBlank(this.email) || isBlank(this.name) || !this.termsAccepted || passwordMismatch;
  }
  validateEmail = (email) => {
    if (email === 'test--' || email === 'test++') { return true; }
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }
  onChangeEmail = (email) => {
    this.invalidEmail = !this.validateEmail(email);
    this.email = email;
  }
  onToggleMailerliteGroup = (groupId) => {
    if (this.selectedMailerliteGroupIds.includes(groupId)) {
      this.selectedMailerliteGroupIds = this.selectedMailerliteGroupIds.filter(id => id !== groupId);
    } else {
      this.selectedMailerliteGroupIds = [...this.selectedMailerliteGroupIds, groupId];
    }
  }
  onSubmit = (e) => {
    const groupId = window.location.href.split('/group/')[1];
    e.preventDefault();
    if (this.disabled) { return; }
    this.submitting = true;
    const params = {
      data: {
        email: this.email,
        name: this.name,
        password: this.password,
        password_confirmation: this.password,
        level_id: this.props.levelId || AppUI.onboardingLevelId,
        language_id: this.props.languageId || AppUI.onboardingLanguageId,
        target_language_id: this.props.targetLanguageId,
        promotion_code: getCookieValue('promotion-code'),
        allow_cookies: getCookieValue('allow-cookies'),
        allow_youtube: getCookieValue('allow-youtube'),
        user_agreed_to_terms: this.termsAccepted,
        user_agreed_to_email: AppUI.siteIsSeedlang ? 'unset' : 'true',
        early_access_token: getCookieValue('early-access-token'),
      },
      queryStrings: {
        group_id: groupId,
      },
    };
    if (AppUI.siteIsMemrev && AppUI.site.enableMailerlite) {
      params.data.mailerlite_group_ids = this.selectedMailerliteGroupIds;
    }
    if (AppUI.user?.id && AppUI.user.guest) {
      AuthenticateStore.convertGuest(params, this.afterRegister);
    } else {
      AuthenticateStore.register(params, this.afterRegister);
    }
  }
  afterRegister = (resp) => {
    this.submitting = false;
    if (resp['alreadyExists']) {
      this.alreadyExists = true;
      AppUI.routeStore.routeTo(`/onboarding/exists?email=${encodeURIComponent(this.email)}`);
    } else if (resp['unconfirmed']) {
      this.props.onConfirmationRequired({
        email: this.email,
        action: 'registration',
      });
    } else {
      if (AppUI.SIMULATE_MOBILE_AUTH) {
        if (this.isBrowser) {
          localStorage.setItem('jwtToken', resp['jwtToken']);
        }
      }
      AppUI.setAuthUserStoreShowData(resp);
      AppUI.set('registrationSubmitted', true);
      if (AppUI.siteIsSeedlang) {
        AppUI.loadReviewDecks();
        TreeUI.loadTreesFromTargetLanguage(AppUI.targetLanguageId);
      }
      if (this.props.onCloseModal) {
        this.props.onCloseModal();
      }
      if (this.props.afterRegistration) {
        this.props.afterRegistration();
      }
    }
  }
  render() {
    return (
      <RegistrationForm
        accentColor={AppUI.site.accentColor}
        buttonBackground={this.buttonBackground}
        color={this.props.color}
        confirmPassword={this.confirmPassword}
        confirmPasswordFocused={this.confirmPasswordFocused}
        disabled={this.disabled}
        email={this.email}
        emailFocused={this.emailFocused}
        enableMailerlite={AppUI.site.enableMailerlite}
        errorMessage={ServerStore.errorMessage}
        hidePrivacy={this.props.hidePrivacy}
        hideTitle={this.props.hideTitle}
        invalidEmail={this.invalidEmail}
        mailerliteGroups={AppUI.site.mailerliteGroupPreferences}
        name={this.name}
        onBlurConfirmPassword={() => this.confirmPasswordFocused = false}
        onBlurEmail={() => this.emailFocused = false}
        onBlurPassword={() => this.passwordFocused = false}
        onChangeConfirmPassword={confirmPassword => this.confirmPassword = confirmPassword}
        onChangeEmail={this.onChangeEmail}
        onChangeName={name => this.name = name}
        onChangePassword={password => this.password = password}
        onFocusConfirmPassword={() => this.confirmPasswordFocused = true}
        onFocusEmail={() => this.emailFocused = true}
        onFocusPassword={() => this.passwordFocused = true}
        onSubmit={this.onSubmit}
        onToggleMailerliteGroup={this.onToggleMailerliteGroup}
        onTogglePasswordVisibility={() => this.unmaskPassword = !this.unmaskPassword}
        onToggleTerms={() => this.termsAccepted = !this.termsAccepted}
        password={this.password}
        passwordFocused={this.passwordFocused}
        safePrivacyUrl={AppUI.site.safePrivacyUrl}
        safeTermsOfServiceUrl={AppUI.site.safeTermsOfServiceUrl}
        selectedMailerliteGroupIds={this.selectedMailerliteGroupIds}
        siteIsSeedlang={AppUI.siteIsSeedlang}
        submitting={this.submitting}
        successMessage={ServerStore.successMessage}
        teamName={AppUI.site.teamName}
        termsAccepted={this.termsAccepted}
        unmaskPassword={this.unmaskPassword}
      />
    );
  }
}

export default RegistrationFormWrapper;
